/**
 * Utility to detect UAT Admin access, enforce immediate logout,
 * and direct users to the official Production Admin portal (admin.myfinfi.com).
 */

export const PROD_ADMIN_URL = 'https://admin.myfinfi.com'

/**
 * Checks if the current page or URL corresponds to uatadmin
 * Matches:
 * - Hostnames containing 'uatadmin' (e.g. uatadmin.myfinfi.com)
 * - Hosts or origins containing 'uatadmin'
 * - Paths starting with '/uatadmin'
 * - Optional query param '?uatadmin=...' or hash '#uatadmin' (for testing)
 * - Optional environment variable VUE_APP_BLOCK_UAT_ADMIN === 'true'
 */
export const isUatAdminEnvironment = () => {
  if (typeof window === 'undefined') return false

  try {
    if (process.env.VUE_APP_BLOCK_UAT_ADMIN === 'true') {
      return true
    }

    const hostname = (window.location.hostname || '').toLowerCase()
    const host = (window.location.host || '').toLowerCase()
    const href = (window.location.href || '').toLowerCase()
    const pathname = (window.location.pathname || '').toLowerCase()
    const search = (window.location.search || '').toLowerCase()
    const hash = (window.location.hash || '').toLowerCase()

    if (
      hostname.includes('uatadmin') ||
      host.includes('uatadmin') ||
      pathname.startsWith('/uatadmin')
    ) {
      return true
    }

    // Support query param / hash testing on local development (e.g. localhost:9002/?uatadmin=1)
    if (search.includes('uatadmin') || hash.includes('uatadmin')) {
      return true
    }

    // Explicit check for uatadmin.myfinfi.com
    if (href.includes('uatadmin.myfinfi.com')) {
      return true
    }
  } catch (e) {
    console.warn('[UAT Admin Check] Error:', e)
  }

  return false
}

/**
 * Performs an immediate, comprehensive logout for any session found on uatadmin
 */
export const performImmediateUatAdminLogout = async () => {
  try {
    const token =
      (typeof localStorage !== 'undefined' &&
        (localStorage.getItem('token') ||
          localStorage.getItem('isAuthenticated'))) ||
      ''

    // 1. Attempt backend session termination via /auth/v3/logout
    try {
      const baseUrl = process.env.VUE_APP_BASE_URL || ''
      const logoutUrl = `${baseUrl.replace(/\/+$/, '')}/auth/v3/logout`
      fetch(logoutUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {})
        },
        credentials: 'include',
        keepalive: true
      }).catch(() => {})
    } catch (_) {
      // ignore network errors during deprecation logout
    }

    // 2. Wipe localStorage
    if (typeof localStorage !== 'undefined') {
      localStorage.clear()
      localStorage.setItem('theme', 'light')
    }

    // 3. Wipe sessionStorage
    if (typeof sessionStorage !== 'undefined') {
      sessionStorage.clear()
    }

    // 4. Invalidate client-accessible cookies on current host and parent domain
    if (typeof document !== 'undefined' && document.cookie) {
      const cookies = document.cookie.split(';')
      const currentHost =
        typeof window !== 'undefined' ? window.location.hostname : ''
      const domains = [
        currentHost,
        `.${currentHost}`,
        '.myfinfi.com',
        'myfinfi.com'
      ]

      for (let i = 0; i < cookies.length; i++) {
        const cookie = cookies[i]
        const eqPos = cookie.indexOf('=')
        const name = eqPos > -1 ? cookie.substr(0, eqPos).trim() : cookie.trim()

        if (name) {
          document.cookie = `${name}=;expires=Thu, 01 Jan 1970 00:00:00 GMT;path=/`
          domains.forEach((d) => {
            if (d) {
              document.cookie = `${name}=;expires=Thu, 01 Jan 1970 00:00:00 GMT;path=/;domain=${d}`
            }
          })
        }
      }
    }

    // 5. Clear CacheStorage
    if (typeof window !== 'undefined' && 'caches' in window) {
      caches
        .keys()
        .then((keys) => {
          keys.forEach((key) => caches.delete(key))
        })
        .catch(() => {})
    }

    // 6. Unregister service workers
    if (typeof navigator !== 'undefined' && 'serviceWorker' in navigator) {
      navigator.serviceWorker
        .getRegistrations()
        .then((registrations) => {
          registrations.forEach((r) => r.unregister())
        })
        .catch(() => {})
    }
  } catch (err) {
    console.warn('[UAT Admin Deprecation Logout]', err)
  }
}

export const redirectToProdAdmin = () => {
  try {
    window.location.href = PROD_ADMIN_URL
  } catch (e) {
    window.open(PROD_ADMIN_URL, '_self')
  }
}
